<template>
  <div>
    <ProgramCardModal
      :programArticle="selectedProgramArticle"
      @close="clearSelectedArticle"
      :isWorkshop="true"
      :userLoggedIn="userLoggedIn"
      :workshopRegistrationOpened="workshopRegistrationOpened"
      @registerWorkshop="registerWorkshop"
      @unRegisterWorkshop="unRegisterWorkshop"
      :scrollToSection="scrollToSection"
    />
    <div class="grid gap-[20px]" :style="'grid-template-columns: repeat('+ Object.keys(workshopsGroupedByHall).length +', minmax(0, 1fr)); }'">
      <div
        :key="key"
        v-for="(group, key) in workshopsGroupedByHall"
      >
        <div class="mb-5">
          <h2 class="font-semibold text-brand-orange text-[24px]">
            {{ workshopHallMap[key] }}
          </h2>
        </div>
        <ProgramCard
          v-for="(workshop, j) in group"
          :key="j"
          class="my-2"
          :programArticle="workshop"
          @clicked="selectProgramArticle"
          @registerWorkshop="registerWorkshop"
          @unRegisterWorkshop="unRegisterWorkshop"
          :isWorkshop="true"
          :userLoggedIn="userLoggedIn"
          :registerGroupsCheck="registerGroupsCheck[key]"
          :workshopRegistrationState="workshopRegistrationStateMutable"
          :maxWorkshopRegistrationsPerUser="maxWorkshopRegistrationsPerUser"
          :workshopRegistrationOpened="workshopRegistrationOpened"
          :workshopsGroupedByTime="workshopsGroupedByTime"
          :text-truncate-chars="35"
        />
      </div>
    </div>
  </div>
</template>

<script>
import Carousel from "../Carousel/Carousel.vue";
import ProgramCard from "../Program/ProgramCard.vue";
import ProgramCardModal from "../Program/ProgramCardModal.vue";
import axios from "axios";
import {parse} from 'date-fns';

export default {
  components: {
    Carousel,
    ProgramCard,
    ProgramCardModal,
  },

  props: {
    programEvents: {
      type: Array,
      default: () => [],
    },

    userLoggedIn: {
      type: Boolean,
      default: false,
    },

    workshopRegistrationState: {
      type: Object,
      default: () => {
      },
    },

    maxWorkshopRegistrationsPerUser: {
      type: Number,
    },

    maxRegistrationsPerWorkshop: {
      type: Number,
      default: 28
    },

    workshopRegistrationOpened: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      selectedProgramArticle: null,
      workshopRegistrationStateMutable: this.workshopRegistrationState,
      registerGroupsCheck: {},
      scrollToSection: false,
      workshopHallMap: {
        ['workshop1']: 'Workshopy 1',
        ['workshop2']: 'Workshopy 2',
        ['workshop3']: 'Workshopy 3',
      },
    };
  },

  watch: {
    workshopRegistrationStateMutable: {
      handler() {
        let times = Object.keys(this.workshopsGroupedByTime);
        times.forEach(startTime => {
          this.registerGroupsCheck[startTime] = this.userHasWorkshopRegisteredForGivenTimeSlot(startTime);
        })
      },
      immediate: true
    }
  },

  computed: {
    workshopsGroupedByTime() {
      const workshops = this.programEvents.filter(
        (event) => event.type === "workshop"
      );

      const grouped = workshops.reduce((groupedWorkshops, workshop) => {

        let parsedWorkshopEnd = parse(workshop.end, "HH:mm", new Date());

        if (groupedWorkshops[parsedWorkshopEnd] == null) {
          groupedWorkshops[parsedWorkshopEnd] = []
        }

        let capacity = this.maxRegistrationsPerWorkshop;

        if (this.workshopRegistrationStateMutable.workshop_registrations[workshop.id]) {
          capacity -= this.workshopRegistrationStateMutable.workshop_registrations[workshop.id].count;
        }

        groupedWorkshops[parsedWorkshopEnd].push({...workshop, capacity: capacity});

        return groupedWorkshops;
      }, {});

      return Object.keys(grouped)
        .sort()
        .reduce((a, c) => ((a[c] = grouped[c]), a), {});
    },
    workshopsGroupedByHall() {
      const workshops = this.programEvents.filter(
        (event) => event.type === "workshop" && ['workshop1', 'workshop2', 'workshop3'].includes(event.hall)
      );

      const grouped = workshops.reduce((groupedWorkshops, workshop) => {

        const parsedWorkshopHall = workshop.hall;

        if (groupedWorkshops[parsedWorkshopHall] == null) {
          groupedWorkshops[parsedWorkshopHall] = []
        }

        let capacity = this.maxRegistrationsPerWorkshop;

        if (this.workshopRegistrationStateMutable.workshop_registrations[workshop.id]) {
          capacity -= this.workshopRegistrationStateMutable.workshop_registrations[workshop.id].count;
        }

        groupedWorkshops[parsedWorkshopHall].push({...workshop, capacity: capacity});

        return groupedWorkshops;
      }, {});

      return Object.keys(grouped)
        .sort()
        .reduce((a, c) => ((a[c] = grouped[c]), a), {});
    }
  },

  methods: {
    clearSelectedArticle() {
      this.selectedProgramArticle = undefined;
      history.pushState(null, '', window.location.pathname);
    },

    userHasWorkshopRegisteredForGivenTimeSlot(workshopStartTime) {
      for (const workshopForTimeSlot of this.workshopsGroupedByTime[workshopStartTime]) {
        for (const registeredWorkshopId of this.workshopRegistrationStateMutable.user_registrations) {
          if (workshopForTimeSlot.id === registeredWorkshopId) {
            return true;
          }
        }
      }

      return false;
    },

    async registerWorkshop(workshopId) {
      try {
        const res = await axios.post(
          `/api/workshop/${workshopId}/registration`,
          {}
        );
        this.workshopRegistrationStateMutable = res.data;
      } catch (err) {
      }
    },

    async unRegisterWorkshop(workshopId) {
      try {
        const res = await axios.delete(
          `/api/workshop/${workshopId}/registration`,
          {}
        );

        this.workshopRegistrationStateMutable = res.data;
      } catch (err) {
      }
    },

    selectProgramArticleAndAddToURL(article) {
      this.selectedProgramArticle = article;

      const urlParams = new URLSearchParams(window.location.search);
      urlParams.set('workshopId', article.id)

      const newRelativePathQuery = window.location.pathname + '?' + urlParams.toString();
      history.pushState(null, '', newRelativePathQuery);
    },

    selectProgramArticle(article) {
      this.scrollToSection = false;
      this.selectProgramArticleAndAddToURL(article);
    },

    selectArticleIfPresentInSearchParams() {
      const urlParams = new URLSearchParams(window.location.search);
      const workshopId = urlParams.get('workshopId');

      if (workshopId !== undefined) {
        this.programEvents.forEach((event) => {
          if (event.id === Number(workshopId)) {
            this.scrollToSection = true;
            this.selectProgramArticleAndAddToURL(event);
          }
        });
      }
    }
  },

  mounted() {
    this.selectArticleIfPresentInSearchParams();
  }
};
</script>

<style>
</style>
